import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, Loader2, AlertTriangle, Users, Shield, LogIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function InviteRegisterForm() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirm: '',
  });

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API_URL}/invites/public/${inviteCode}`);
        setInviteInfo(res.data);
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) {
          setError(err.response?.data?.detail || 'Convite expirado ou esgotado.');
        } else {
          setError('Convite invalido.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!form.name || !form.email || !form.password) {
      setFormError('Todos os campos sao obrigatorios.');
      return;
    }
    if (form.password.length < 6) {
      setFormError('A password deve ter pelo menos 6 caracteres.');
      return;
    }
    if (form.password !== form.password_confirm) {
      setFormError('As passwords nao coincidem.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/invites/public/${inviteCode}/register`, {
        name: form.name,
        email: form.email,
        password: form.password,
      });
      setRegistered(true);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Erro ao criar conta.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Convite Indisponivel</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (registered) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0" data-testid="invite-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Conta Criada!</h2>
            <p className="text-slate-500 mb-6">
              A sua conta foi criada com sucesso. Ja pode aceder ao CRM.
            </p>
            <a
              href="/login"
              className="inline-flex items-center justify-center w-full gap-2 bg-[#2D3E6F] text-white text-sm font-medium py-3 px-4 rounded-lg hover:bg-[#1a2a4a] transition-colors"
              data-testid="goto-login-btn"
            >
              <LogIn className="w-4 h-4" />
              Entrar no CRM
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Criar Conta</h1>
          <p className="text-slate-300 mt-1">Convite para juntar-se a equipa</p>
        </div>

        <Card className="shadow-2xl border-0" data-testid="invite-register-card">
          {/* Invite info banner */}
          <div className="bg-blue-50 border-b border-blue-100 p-4 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#2D3E6F] rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-800">{inviteInfo.role_label}</div>
                <div className="text-xs text-slate-500 space-x-2">
                  {inviteInfo.team_name && <span>Equipa: {inviteInfo.team_name}</span>}
                  {inviteInfo.partner_name && <span>Parceiro: {inviteInfo.partner_name}</span>}
                </div>
              </div>
            </div>
            {inviteInfo.created_by_name && (
              <p className="text-xs text-slate-400 mt-2">Convidado por {inviteInfo.created_by_name}</p>
            )}
          </div>

          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-slate-600">Nome Completo *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(p => ({...p, name: e.target.value}))}
                  placeholder="O seu nome completo"
                  required
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-600">Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(p => ({...p, email: e.target.value}))}
                  placeholder="seu@email.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <Label className="text-slate-600">Password *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm(p => ({...p, password: e.target.value}))}
                  placeholder="Minimo 6 caracteres"
                  required
                  data-testid="invite-password-input"
                />
              </div>
              <div>
                <Label className="text-slate-600">Confirmar Password *</Label>
                <Input
                  type="password"
                  value={form.password_confirm}
                  onChange={(e) => setForm(p => ({...p, password_confirm: e.target.value}))}
                  placeholder="Repetir password"
                  required
                  data-testid="invite-password-confirm-input"
                />
              </div>

              {formError && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="invite-form-error">
                  <AlertTriangle className="w-4 h-4" />
                  {formError}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#2D3E6F] hover:bg-[#1a2a4a] py-6 text-base"
                data-testid="invite-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A criar conta...
                  </>
                ) : 'Criar Conta'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
